import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { Toaster, ToastBar, toast as hotToast } from 'react-hot-toast'
import { QueryClientProvider } from '@tanstack/react-query'
import { useAuthStore } from '@/store/authStore'
import { useThemeStore } from '@/store/themeStore'
import { queryClient } from '@/lib/queryClient'

// Layouts
import AppLayout    from '@/components/Layout/AppLayout'
import PublicLayout from '@/components/Layout/PublicLayout'
import BrandingProvider from '@/components/BrandingProvider'
import SessionTimeout from '@/components/SessionTimeout'
import ScrollToTop from '@/components/ScrollToTop'
import NavigationBridge from '@/components/NavigationBridge'

// Public pages
const LandingPage = lazy(() => import('@/pages/Public/LandingPage'))
const PricingPage = lazy(() => import('@/pages/Public/PricingPage'))
const FeaturesPage = lazy(() => import('@/pages/Public/FeaturesPage'))
const AboutPage = lazy(() => import('@/pages/Public/AboutPage'))
const ContactPage = lazy(() => import('@/pages/Public/ContactPage'))
const DemoPage = lazy(() => import('@/pages/Public/DemoPage'))
const SecurityPage = lazy(() => import('@/pages/Public/SecurityPage'))
const TermsPage = lazy(() => import('@/pages/Public/TermsPage'))
const PrivacyPage = lazy(() => import('@/pages/Public/PrivacyPage'))
const NotFoundPage = lazy(() => import('@/pages/Public/NotFoundPage'))
const PatientPortalPage = lazy(() => import('@/pages/Public/PatientPortalPage'))
import ErrorBoundary from '@/components/ErrorBoundary'
const IntegrationsPage = lazy(() => import('@/pages/Settings/IntegrationsPage'))

// Auth pages
const LoginPage = lazy(() => import('@/pages/Auth/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/Auth/RegisterPage'))
const VerifyOtpPage = lazy(() => import('@/pages/Auth/VerifyOtpPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/Auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/Auth/ResetPasswordPage'))

// App pages
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const MeetingsPage = lazy(() => import('@/pages/Meetings/MeetingsPage'))
const MeetingPage = lazy(() => import('@/pages/Meetings/MeetingPage'))
const NewMeetingPage = lazy(() => import('@/pages/Meetings/NewMeetingPage'))
const DraftsPage = lazy(() => import('@/pages/DraftsPage'))
const SearchPage = lazy(() => import('@/pages/SearchPage'))
const SectionsPage = lazy(() => import('@/pages/SectionsPage'))
const ParametersPage = lazy(() => import('@/pages/ParametersPage'))
const AiSettingsPage = lazy(() => import('@/pages/AiSettingsPage'))
const ApiKeyGuidePage = lazy(() => import('@/pages/ApiKeyGuidePage'))
const BillingPage = lazy(() => import('@/pages/BillingPage'))
const TemplatesPage = lazy(() => import('@/pages/Templates/TemplatesPage'))
const ClinicAnalyticsPage = lazy(() => import('@/pages/Clinic/ClinicAnalyticsPage'))
const ReferralPage = lazy(() => import('@/pages/ReferralPage'))
const WhiteLabelPage = lazy(() => import('@/pages/Clinic/WhiteLabelPage'))
const PatientsPage = lazy(() => import('@/pages/Patients/PatientsPage'))
const PatientPage = lazy(() => import('@/pages/Patients/PatientPage'))
const ClinicalFlagsPage = lazy(() => import('@/pages/Patients/ClinicalFlagsPage'))
const SchedulingPage = lazy(() => import('@/pages/SchedulingPage'))
const LabOrdersPage = lazy(() => import('@/pages/LabOrdersPage'))
const PrescriptionsPage = lazy(() => import('@/pages/PrescriptionsPage'))
const AdminDashboardPage = lazy(() => import('@/pages/Admin/AdminDashboardPage'))
const RevenuePage = lazy(() => import('@/pages/Admin/RevenuePage'))
const AiUsagePage = lazy(() => import('@/pages/Admin/AiUsagePage'))
const ProductivityPage = lazy(() => import('@/pages/Admin/ProductivityPage'))
const SystemHealthPage = lazy(() => import('@/pages/Admin/SystemHealthPage'))
const SubscriptionLifecyclePage = lazy(() => import('@/pages/Admin/SubscriptionLifecyclePage'))
const OnboardingFunnelPage = lazy(() => import('@/pages/Admin/OnboardingFunnelPage'))
const BroadcastPage = lazy(() => import('@/pages/Admin/BroadcastPage'))
const HipaaPage = lazy(() => import('@/pages/Admin/HipaaPage'))
const PartnersPage = lazy(() => import('@/pages/Admin/PartnersPage'))
const BulkActionsPage = lazy(() => import('@/pages/Admin/BulkActionsPage'))
const TemplatesModerationPage = lazy(() => import('@/pages/Admin/TemplatesModerationPage'))
const PricingConfigPage = lazy(() => import('@/pages/Admin/PricingConfigPage'))
const BannerManagerPage = lazy(() => import('@/pages/Admin/BannerManagerPage'))
const FeatureFlagsPage = lazy(() => import('@/pages/Admin/FeatureFlagsPage'))
const DataExportPage = lazy(() => import('@/pages/Admin/DataExportPage'))
const FeatureRoadmapPage = lazy(() => import('@/pages/Admin/FeatureRoadmapPage'))
const UsersPage = lazy(() => import('@/pages/Admin/UsersPage'))
const AuditLogPage = lazy(() => import('@/pages/Admin/AuditLogPage'))
const PaymentConfigPage = lazy(() => import('@/pages/Admin/PaymentConfigPage'))
const UpiPaymentsPage = lazy(() => import('@/pages/Admin/UpiPaymentsPage'))
const UpiAccountsPage = lazy(() => import('@/pages/Admin/UpiAccountsPage'))
const SecuritySettingsPage = lazy(() => import('@/pages/Admin/SecuritySettingsPage'))
const PaymentSettings = lazy(() => import('@/pages/Admin/PaymentSettings'))
const IntegrationSettings = lazy(() => import('@/pages/Admin/IntegrationSettings'))
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
import ComingSoonPage from '@/pages/ComingSoonPage'

// Shown while the auth check settles and while a route's chunk downloads.
function RouteFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#090e0b' }}>
      <div style={{ width: 32, height: 32, border: '2px solid #533AB7', borderTopColor: 'transparent', borderRadius: '50%', animation: 'pub-spin-slow 0.7s linear infinite' }} />
    </div>
  )
}

function PrivateRoute({ children }) {
  const { token, loading } = useAuthStore()
  if (loading) return <RouteFallback />
  return token ? children : <Navigate to="/login" replace />
}

// Platform-admin lockdown for /admin/*. The sidebar already hides the admin
// group for everyone else and the API enforces it server-side, but without
// this a non-admin could reach the full admin shell by typing the URL and
// would just see every panel fail to load. Renders as a pathless layout route
// so all 25 admin paths inherit it. `loading` is already settled here — this
// tree only mounts inside PrivateRoute, which holds the spinner until init()
// has resolved the user.
function AdminRoute() {
  const user = useAuthStore(s => s.user)
  if (user?.role !== 'platform_admin') return <Navigate to="/dashboard" replace />
  return <Outlet />
}

function PublicRoute({ children }) {
  const { token } = useAuthStore()
  return !token ? children : <Navigate to="/dashboard" replace />
}

export default function App() {
  const init = useAuthStore(s => s.init)
  const theme = useThemeStore(s => s.theme)
  useEffect(() => { init() }, [init])

  // Apply theme class to html element
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark')
    document.documentElement.classList.add(theme)
  }, [theme])

  // Pre-launch gate — when VITE_COMING_SOON=true the whole app is hidden
  // behind a static landing page. Flip the flag in .env.<env> and rebuild
  // to bring the real app back.
  if (import.meta.env.VITE_COMING_SOON === 'true') {
    return <ComingSoonPage />
  }

  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrandingProvider>
      <BrowserRouter>
        <ScrollToTop />
        <NavigationBridge />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public pages with shared nav/footer */}
          <Route element={<PublicLayout />}>
            <Route path="/"         element={<LandingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing"  element={<PricingPage />} />
            <Route path="/about"    element={<AboutPage />} />
            <Route path="/contact"  element={<ContactPage />} />
            <Route path="/demo"     element={<DemoPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/terms"    element={<TermsPage />} />
            <Route path="/privacy"  element={<PrivacyPage />} />
          </Route>

          {/* Patient portal (public, no auth). Wrapped in an error
              boundary so a JS crash inside the portal renders as a
              friendly card instead of an empty <body> — QA hit the
              blank-page bug twice on this route; the boundary is
              belt-and-suspenders once the destructuring guard already
              inside the page has done its part. */}
          <Route path="/portal/:token" element={<ErrorBoundary><PatientPortalPage /></ErrorBoundary>} />

          {/* Auth pages (no shared layout) */}
          <Route path="/login"           element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register"        element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password"  element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
          <Route path="/verify-otp"      element={<VerifyOtpPage />} />

          {/* Protected app */}
          <Route path="/" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
            <Route path="dashboard"                   element={<DashboardPage />} />
            <Route path="profile"                     element={<ProfilePage />} />
            <Route path="settings/integrations"       element={<IntegrationsPage />} />
            <Route path="meetings"                    element={<MeetingsPage />} />
            <Route path="meetings/new"                element={<NewMeetingPage />} />
            <Route path="drafts"                       element={<DraftsPage />} />
            <Route path="search"                       element={<SearchPage />} />
            <Route path="meetings/:id"                element={<MeetingPage />} />
            <Route path="meetings/:id/transcript"     element={<MeetingPage tab="transcript" />} />
            <Route path="meetings/:id/insights"       element={<MeetingPage tab="insights" />} />
            <Route path="meetings/:id/ask"            element={<MeetingPage tab="ask" />} />
            <Route path="meetings/:id/email"          element={<MeetingPage tab="email" />} />
            <Route path="meetings/:id/clinical"       element={<MeetingPage tab="clinical" />} />
            <Route path="meetings/:id/billing"        element={<MeetingPage tab="billing" />} />
            <Route path="meetings/:id/preauth"        element={<MeetingPage tab="preauth" />} />
            <Route path="meetings/:id/exercises"      element={<MeetingPage tab="exercises" />} />
            <Route path="meetings/:id/attachments"    element={<MeetingPage tab="attachments" />} />
            <Route path="meetings/:id/playback"       element={<MeetingPage tab="playback" />} />
            <Route path="analytics"                    element={<AnalyticsPage />} />
            <Route path="clinic"                      element={<ClinicAnalyticsPage />} />
            <Route path="referrals"                   element={<ReferralPage />} />
            <Route path="patients"                    element={<PatientsPage />} />
            <Route path="clinical-flags"              element={<ClinicalFlagsPage />} />
            <Route path="scheduling"                  element={<SchedulingPage />} />
            <Route path="lab-orders"                  element={<LabOrdersPage />} />
            <Route path="prescriptions"               element={<PrescriptionsPage />} />
            <Route path="patients/:id"               element={<PatientPage />} />
            <Route path="sections"                    element={<SectionsPage />} />
            <Route path="parameters"                  element={<ParametersPage />} />
            <Route path="templates"                   element={<TemplatesPage />} />
            <Route path="ai-settings"                  element={<AiSettingsPage />} />
            <Route path="api-key-guide"                element={<ApiKeyGuidePage />} />
            <Route path="billing"                      element={<BillingPage />} />
            <Route path="white-label"                  element={<WhiteLabelPage />} />
            <Route element={<AdminRoute />}>
              <Route path="admin"                          element={<AdminDashboardPage />} />
              <Route path="admin/dashboard"                element={<AdminDashboardPage />} />
              <Route path="admin/revenue"                  element={<RevenuePage />} />
              <Route path="admin/ai-usage"                 element={<AiUsagePage />} />
              <Route path="admin/productivity"             element={<ProductivityPage />} />
              <Route path="admin/system-health"            element={<SystemHealthPage />} />
              <Route path="admin/subscription-lifecycle"   element={<SubscriptionLifecyclePage />} />
              <Route path="admin/onboarding-funnel"        element={<OnboardingFunnelPage />} />
              <Route path="admin/broadcast"                element={<BroadcastPage />} />
              <Route path="admin/hipaa"                    element={<HipaaPage />} />
              <Route path="admin/partners"                 element={<PartnersPage />} />
              <Route path="admin/bulk-actions"             element={<BulkActionsPage />} />
              <Route path="admin/templates"                element={<TemplatesModerationPage />} />
              <Route path="admin/pricing"                  element={<PricingConfigPage />} />
              <Route path="admin/banners"                  element={<BannerManagerPage />} />
              <Route path="admin/feature-flags"            element={<FeatureFlagsPage />} />
              <Route path="admin/data-export"              element={<DataExportPage />} />
              <Route path="admin/roadmap"                  element={<FeatureRoadmapPage />} />
              <Route path="admin/users"                   element={<UsersPage />} />
              <Route path="admin/audit-logs"               element={<AuditLogPage />} />
              <Route path="admin/payments"                 element={<PaymentConfigPage />} />
              <Route path="admin/payment-settings"         element={<PaymentSettings />} />
              <Route path="admin/integration-settings"     element={<IntegrationSettings />} />
              <Route path="admin/upi-payments"             element={<UpiPaymentsPage />} />
              <Route path="admin/upi-accounts"             element={<UpiAccountsPage />} />
              <Route path="admin/security-settings"        element={<SecuritySettingsPage />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </BrandingProvider>

      <SessionTimeout />
      <Toaster position="top-right" toastOptions={{
        // Auto-dismiss durations: keep success snappy, cap errors at 5s.
        // QA filed 'Validation failed' toasts lingering + no way to
        // dismiss — this + the ToastBar click handler below closes it.
        duration: 4000,
        success: {
          duration: 3000,
          iconTheme: { primary: '#00D4B5', secondary: theme === 'dark' ? '#1a261e' : '#ffffff' },
        },
        error: {
          duration: 5000,
          iconTheme: { primary: '#ef4444', secondary: theme === 'dark' ? '#1a261e' : '#ffffff' },
        },
        style: theme === 'dark'
          ? { background: '#1a261e', color: '#e2e8f0', border: '1px solid #2b3a31', borderRadius: '10px', fontSize: '13px', cursor: 'pointer' }
          : { background: '#ffffff', color: '#1f2937', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '13px', cursor: 'pointer' },
      }}>
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              // Click anywhere on the toast to dismiss it. react-hot-toast
              // doesn't ship a close button by default; wrapping the
              // default ToastBar with a click handler gets both the
              // click-to-dismiss and any custom-content toast (like the
              // 'Session limit reached' JSX one) for free.
              <div
                onClick={() => hotToast.dismiss(t.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 4px 4px 8px' }}
              >
                {icon}
                <div style={{ flex: 1 }}>{message}</div>
              </div>
            )}
          </ToastBar>
        )}
      </Toaster>
    </QueryClientProvider>
    </ErrorBoundary>
  )
}
